<template>
  <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" @click.self="close">
    <div class="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
      <div class="flex items-center justify-between px-5 py-3 border-b">
        <h3 class="text-lg font-semibold text-slate-800">
          <i class="fas fa-camera mr-1"></i>
          Attach Site Media
        </h3>
        <button type="button" class="text-slate-400 hover:text-slate-700" @click="close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="px-5 py-3 border-b flex flex-wrap gap-2 items-center text-xs">
        <span class="text-slate-600">Filter:</span>
        <button
          v-for="opt in typeFilters"
          :key="opt.id"
          type="button"
          class="px-2 py-1 rounded border"
          :class="activeType === opt.id ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300'"
          @click="activeType = opt.id"
        >
          {{ opt.label }}
        </button>
        <div class="ml-auto">
          <input
            v-model="searchTerm"
            type="search"
            placeholder="Search notes / flags"
            class="px-2 py-1 border border-slate-300 rounded text-sm"
          />
        </div>
      </div>

      <div class="flex-1 overflow-auto p-4">
        <div v-if="loading" class="py-10 text-center text-slate-500 text-sm">Loading captures…</div>
        <div v-else-if="!filteredItems.length" class="py-10 text-center text-slate-400 text-sm">
          No captures match.
        </div>
        <div v-else class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          <label
            v-for="item in filteredItems"
            :key="item.id"
            class="relative aspect-square rounded-lg overflow-hidden border cursor-pointer"
            :class="selectedIds.includes(item.id) ? 'ring-2 ring-indigo-500 border-indigo-500' : 'border-slate-200'"
          >
            <input
              type="checkbox"
              class="absolute top-1.5 left-1.5 z-10"
              :value="item.id"
              v-model="selectedIds"
            />
            <img
              v-if="item.thumbnail_url || item.file_url"
              :src="item.thumbnail_url || item.file_url"
              alt=""
              class="w-full h-full object-cover"
              loading="lazy"
            />
            <div v-else class="w-full h-full flex items-center justify-center bg-slate-100 text-3xl">
              <span v-if="item.media_type === 'video'">🎥</span>
              <span v-else-if="item.media_type === 'audio'">🎤</span>
              <span v-else>📎</span>
            </div>
            <div class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent text-white text-[10px] px-1.5 py-1">
              <div class="truncate">{{ formatDate(item.captured_at) }}</div>
              <div v-if="(item.ai_flags || []).length" class="text-amber-200 truncate">
                {{ item.ai_flags.slice(0, 2).join(', ') }}
              </div>
            </div>
          </label>
        </div>
      </div>

      <div class="px-5 py-3 border-t flex items-center justify-between">
        <div class="text-sm text-slate-500">
          {{ selectedIds.length }} selected
        </div>
        <div class="flex gap-2">
          <button
            type="button"
            class="px-3 py-1.5 rounded border border-slate-300 text-sm"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="button"
            class="px-3 py-1.5 rounded bg-indigo-600 text-white text-sm disabled:opacity-50"
            :disabled="!selectedIds.length || attaching"
            @click="confirmAttach"
          >
            {{ attaching ? 'Attaching…' : 'Attach selected' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { listSiteMedia, attachSiteMedia } from '@/services/siteMediaService'

/**
 * SiteMediaAttachModal — picks existing site-media records and attaches them
 * to a CheckRequirement (or BankDraw / UniversalContext via props).
 *
 * Props:
 *   - modelValue: boolean (v-model open/close)
 *   - projectId: number (required to filter the list)
 *   - checkRequirementId, bankDrawId, relatedContextId: optional attach targets
 *
 * Emits:
 *   - update:modelValue(false) on close
 *   - attached(records: SiteMedia[])
 */
export default {
  name: 'SiteMediaAttachModal',
  props: {
    modelValue: { type: Boolean, default: false },
    projectId: { type: [Number, String], default: null },
    checkRequirementId: { type: [Number, String], default: null },
    bankDrawId: { type: [Number, String], default: null },
    relatedContextId: { type: [Number, String], default: null },
  },
  emits: ['update:modelValue', 'attached'],
  data() {
    return {
      items: [],
      selectedIds: [],
      loading: false,
      attaching: false,
      activeType: 'all',
      searchTerm: '',
      typeFilters: [
        { id: 'all', label: 'All' },
        { id: 'photo', label: 'Photos' },
        { id: 'video', label: 'Video' },
        { id: 'audio', label: 'Audio' },
      ],
    }
  },
  computed: {
    filteredItems() {
      const q = this.searchTerm.trim().toLowerCase()
      return this.items.filter((it) => {
        if (this.activeType !== 'all' && it.media_type !== this.activeType) return false
        if (!q) return true
        const hay = [
          it.notes,
          it.transcript,
          (it.ai_flags || []).join(' '),
          it.captured_by_name,
        ].filter(Boolean).join(' ').toLowerCase()
        return hay.includes(q)
      })
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.load()
      else this.selectedIds = []
    },
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false)
    },
    formatDate(iso) {
      if (!iso) return ''
      try {
        return new Date(iso).toLocaleString(undefined, {
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        })
      } catch (_) { return iso }
    },
    async load() {
      if (!this.projectId) return
      this.loading = true
      try {
        const data = await listSiteMedia({ project: this.projectId })
        this.items = Array.isArray(data) ? data : (data?.results || [])
      } catch (err) {
        console.error('Failed to list site media:', err)
        this.items = []
      } finally {
        this.loading = false
      }
    },
    async confirmAttach() {
      if (!this.selectedIds.length) return
      this.attaching = true
      const attached = []
      try {
        for (const id of this.selectedIds) {
          const rec = await attachSiteMedia(id, {
            checkRequirementId: this.checkRequirementId || undefined,
            bankDrawId: this.bankDrawId || undefined,
            relatedContextId: this.relatedContextId || undefined,
          })
          attached.push(rec)
        }
        this.$emit('attached', attached)
        this.$emit('update:modelValue', false)
      } catch (err) {
        console.error('Attach failed:', err)
        alert('Failed to attach site media. See console.')
      } finally {
        this.attaching = false
      }
    },
  },
}
</script>
